import { ArrowUpRight, Code2, Cpu, LayoutDashboard, Plug, Car, CreditCard, Zap, Truck, Home as HomeIcon, Sun, Layers, MapPin, Compass, BedDouble, Users } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang } from "@/lib/i18n";

const Group = ({ icon: Icon, title, items }) => (
  <div className="border border-white/10 p-6 hover:border-gold/40 transition-colors duration-500 bg-ink/60">
    <div className="flex items-center gap-3 mb-4">
      <Icon size={16} className="text-gold" />
      <h4 className="font-mono text-[11px] tracking-[0.25em] uppercase text-ivory">{title}</h4>
    </div>
    <ul className="space-y-1.5">
      {items.map((it) => (
        <li key={it} className="text-sm text-white/50">{it}</li>
      ))}
    </ul>
  </div>
);

const CTA = ({ href, label, testid, external }) => (
  <a
    data-testid={testid}
    href={href}
    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    className="group inline-flex items-center gap-2 mt-10 font-mono text-[11px] tracking-[0.25em] uppercase text-gold hover:text-gold-light transition-colors"
  >
    {label}
    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
  </a>
);

function DivisionShell({ id, index, label, heading, headingItalic, desc, children, cta, image, flip }) {
  return (
    <div id={id} data-testid={`${id}-section`} className="py-24 lg:py-32 border-b border-white/10 last:border-b-0">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14 items-start">
        <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
          <Reveal className="flex items-center gap-4 mb-8">
            <span className="font-mono text-[11px] tracking-[0.3em] text-gold">{index}</span>
            <span className="h-px w-10 bg-gold/60" />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/50">{label}</span>
          </Reveal>
          <Reveal>
            <h3 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight text-ivory">
              {heading} {headingItalic && <span className="italic text-gold-light">{headingItalic}</span>}
            </h3>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-6 text-white/60 leading-relaxed text-base md:text-lg">{desc}</p>
          </Reveal>
          <Reveal delay={0.2}>{cta}</Reveal>
        </div>
        <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
          {image && (
            <Reveal className="mb-8">
              <div className="relative h-56 lg:h-64 overflow-hidden border border-white/10 group">
                <img src={image} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-ink/50" />
              </div>
            </Reveal>
          )}
          <div className="grid sm:grid-cols-2 gap-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

function HxCard({ icon: Icon, name, desc, tag, live }) {
  return (
    <div className={`border p-6 transition-colors duration-500 bg-ink/60 ${live ? "border-forest/60 hover:border-gold/50" : "border-white/10 hover:border-gold/40"}`}>
      <div className="flex items-center justify-between mb-4">
        <Icon size={16} className="text-gold" />
        <span className={`flex items-center gap-1.5 font-mono text-[9px] tracking-[0.2em] uppercase ${live ? "text-emerald-400/90" : "text-white/35"}`}>
          {live && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
          {tag}
        </span>
      </div>
      <h4 className="font-serif text-xl text-ivory">{name}</h4>
      <p className="mt-1.5 text-sm text-white/50">{desc}</p>
    </div>
  );
}

export default function Divisions() {
  const { t, list } = useLang();
  return (
    <section data-testid="divisions-section" className="relative">
      <DivisionShell
        id="division-01"
        index="03"
        label={t("digital.label")}
        heading={t("digital.ha")}
        headingItalic={t("digital.hb")}
        desc={t("digital.desc")}
        cta={<CTA href="#contact" label={t("digital.cta")} testid="digital-cta" />}
      >
        <Group icon={Code2} title={t("digital.g1")} items={list("digital.g1.i")} />
        <Group icon={Cpu} title={t("digital.g2")} items={list("digital.g2.i")} />
        <Group icon={LayoutDashboard} title={t("digital.g3")} items={list("digital.g3.i")} />
        <Group icon={Plug} title={t("digital.g4")} items={list("digital.g4.i")} />
      </DivisionShell>

      <DivisionShell
        id="division-02"
        index="04"
        label={t("hx.label")}
        heading="HX"
        headingItalic="ECOSYSTEMS"
        desc={t("hx.desc")}
        image="https://images.unsplash.com/photo-1513061379709-ef0cd1695189?crop=entropy&cs=srgb&fm=jpg&q=85"
        flip
        cta={<CTA href="https://hx.bsgotech.com" label={t("hx.cta")} testid="hx-cta" external />}
      >
        {[1, 2, 3, 4].map((n) => (
          <HxCard
            key={n}
            icon={[Car, CreditCard, Zap, Truck][n - 1]}
            name={t(`hx.${n}.name`)}
            desc={t(`hx.${n}.desc`)}
            tag={n <= 2 ? t("hx.live") : t("hx.future")}
            live={n <= 2}
          />
        ))}
      </DivisionShell>

      <DivisionShell
        id="division-03"
        index="05"
        label={t("modular.label")}
        heading="MODULAR"
        headingItalic="ENERGY"
        desc={t("modular.desc")}
        image="https://images.unsplash.com/photo-1674758978719-6121d8fbefb4?crop=entropy&cs=srgb&fm=jpg&q=85"
        cta={<CTA href="https://stp.bsgotech.com" label={t("modular.cta")} testid="modular-cta" external />}
      >
        <Group icon={HomeIcon} title={t("modular.g1")} items={list("modular.g1.i")} />
        <Group icon={Sun} title={t("modular.g2")} items={list("modular.g2.i")} />
        <Group icon={Layers} title={t("modular.g3")} items={list("modular.g3.i")} />
        <div className="border border-gold/30 bg-gold/[0.04] p-6 flex flex-col justify-center">
          <h4 className="font-mono text-[11px] tracking-[0.25em] uppercase text-gold mb-3">{t("modular.pos.t")}</h4>
          <p className="text-sm text-white/60 leading-relaxed">{t("modular.pos")}</p>
        </div>
      </DivisionShell>

      <DivisionShell
        id="division-04"
        index="06"
        label={t("eco.label")}
        heading="ECO"
        headingItalic="COLLECTION"
        desc={t("eco.desc")}
        flip
        cta={<CTA href="https://ecocollection.bsgotech.com" label={t("eco.cta")} testid="eco-cta" external />}
      >
        <Group icon={MapPin} title={t("eco.g1")} items={list("eco.g1.i")} />
        <Group icon={Compass} title={t("eco.g2")} items={list("eco.g2.i")} />
        <Group icon={BedDouble} title={t("eco.g3")} items={list("eco.g3.i")} />
        <Group icon={Users} title={t("eco.g4")} items={list("eco.g4.i")} />
      </DivisionShell>
    </section>
  );
}
